import { useLocation } from 'js/lib/useRouter';

import { supportedSubfolderLocaleCodes } from 'bundles/internationalization-lib/constants/subfolderLocaleCodes';
import { urlPathLocaleSubfolderPatternMatcher } from 'bundles/internationalization-lib/utils/subfolderPatternMatcher';

/**
 * Hook that extracts the language and locale information from the URL path using a pattern matcher.
 *
 * It relies on `useLocation` to get the current location object from the React Router,
 * and applies a pattern matcher to extract the language and locale information
 * from the pathname of the location object.
 *
 * It checks whether the extracted locale from the pathname is part of the supported subfolder locale codes.
 * If supported, returns the language and locale code, otherwise returns undefined.
 *
 * @returns {string|undefined} The language and locale string extracted from the URL's subfolder pattern, if present and part of supported subfolders; otherwise `undefined`.
 */
const useSubfolderLanguageAndLocale = (): string | undefined => {
  const location = useLocation();
  const languageAndLocale = urlPathLocaleSubfolderPatternMatcher(location.pathname)?.languageAndLocale;

  return languageAndLocale && supportedSubfolderLocaleCodes.includes(languageAndLocale) ? languageAndLocale : undefined;
};

export default useSubfolderLanguageAndLocale;
